import type { AdminAddonGroup, AdminCategory, AdminItem, LocalizedPair } from '@diora/contracts';
import { editablePair } from '@diora/contracts';
import { useQuery } from '@tanstack/react-query';
import { Ban, ClipboardList, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PageHeader } from '@/components/app-shell';
import { Money } from '@/components/money';
import { BilingualField } from '@/components/bilingual';
import { FormLoading, FormPage, RecordPage, useDirty, useRecord } from '@/components/form-page';
import { ImagePicker } from '@/components/image-picker';
import {
  AvailabilityBadge,
  PublishBlockers,
  PublishWarnings,
  RowActions,
  StatusBadge,
  TranslationBadge,
} from '@/components/records';
import {
  Button,
  Checkbox,
  DataTable,
  EmptyState,
  Field,
  Input,
  Pagination,
  Select,
  SkeletonRows,
  Switch,
  Toolbar,
} from '@/components/ui';
import {
  apiGet,
  apiPatch,
  apiPost,
  apiPut,
  useAdminList,
  useAdminMutation,
  useLifecycle,
  useListParams,
} from '@/hooks/admin';
import { qk } from '@/lib/query';
import { useAuth } from '@/providers/auth';
import { useLocale } from '@/providers/locale';
import { ListFilters } from './_filters';

const URL = '/admin/menu/items';
/** The list this screen's form pages return to. */
const LIST = '/menu/items';
const INVALIDATE = [['items'], ['item'], qk.dashboard];

/** Both the list's filter and the form's picker need the whole category list. */
function useCategoryOptions() {
  return useQuery({
    queryKey: qk.categories({ all: true }),
    queryFn: () => apiGet<AdminCategory[]>('/admin/menu/categories', { page: 1, pageSize: 100 }),
  });
}

export function ItemsPage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { can } = useAuth();
  const canEdit = can('OWNER', 'EDITOR');

  const [params, setParams] = useListParams();
  const [categoryId, setCategoryId] = useState<string>('');
  const query = { ...params, ...(categoryId ? { categoryId } : {}) };

  const list = useAdminList<AdminItem>(qk.items(query), URL, query);
  const invalidate = INVALIDATE;
  const lifecycle = useLifecycle(URL, invalidate);

  const categories = useCategoryOptions();

  const availability = useAdminMutation(
    ({ row, next }: { row: AdminItem; next: 'AVAILABLE' | 'UNAVAILABLE' }) =>
      apiPost(`${URL}/${row.id}/availability`, {
        expectedVersion: row.version,
        availabilityStatus: next,
      }),
    { successMessage: t('common.saved'), invalidate },
  );

  return (
    <>
      <PageHeader
        title={t('items.title')}
        description={t('items.subtitle')}
        actions={
          canEdit && (
            <Link to={`${LIST}/new`} className="dio-btn dio-btn--primary">
              <Plus className="dio-icon" aria-hidden="true" />
              {t('items.newTitle')}
            </Link>
          )
        }
      />

      <Toolbar>
        <ListFilters params={params} onChange={setParams}>
          <Select
            value={categoryId}
            aria-label={t('items.filterByCategory')}
            onChange={(event) => {
              setCategoryId(event.target.value);
              setParams({ page: 1 });
            }}
          >
            <option value="">{t('items.filterByCategory')}</option>
            {(categories.data?.data ?? []).map((category) => (
              <option key={category.id} value={category.id}>
                {category.name[locale] || category.name.ar || category.name.en}
              </option>
            ))}
          </Select>
        </ListFilters>
      </Toolbar>

      {list.isLoading ? (
        <SkeletonRows />
      ) : list.rows.length === 0 ? (
        <EmptyState
          icon={<ClipboardList aria-hidden="true" />}
          title={params.search ? t('common.noResults') : t('items.empty')}
          hint={params.search ? t('common.noResultsHint') : t('items.emptyHint')}
        />
      ) : (
        <DataTable
          caption={t('items.title')}
          columns={[
            <span className="sr-only">{t('common.image')}</span>,
            t('items.name'),
            t('categories.title'),
            t('items.basePrice'),
            t('common.status'),
            <span className="sr-only">{t('common.actions')}</span>,
          ]}
        >
          {list.rows.map((row) => (
            <tr key={row.id}>
              <td style={{ inlineSize: '1%' }}>
                {row.image ? (
                  <img src={row.image.thumbUrl} alt="" className="dio-table__thumb" />
                ) : (
                  // A published dish always has a photo (C-10), so an empty
                  // cell here is a draft — the placeholder keeps the column
                  // aligned instead of letting the row jump.
                  <span className="dio-table__thumb" aria-hidden="true" />
                )}
              </td>

              <td>
                {/* Both languages, because which one is missing is the thing an
                    editor is scanning this column for. */}
                <div className="dio-table__name flex flex-wrap items-center gap-2">
                  <span lang="ar" dir="rtl">
                    {row.name.ar || '—'}
                  </span>
                  <span className="dio-meta" aria-hidden="true">
                    /
                  </span>
                  <span lang="en" dir="ltr">
                    {row.name.en || '—'}
                  </span>
                </div>
                <TranslationBadge translation={row.translation} />
              </td>

              <td className="dio-table__sub">
                {row.category.name[locale] || row.category.name.ar || row.category.name.en}
              </td>

              <td className="dio-table__num">
                {/* A price with no currency yet is still a number worth
                    showing — the API simply refuses to publish it. */}
                <Money amount={row.basePrice} fallback={t('items.noPrice')} />
              </td>

              <td>
                <div className="flex flex-wrap items-center gap-1">
                  <StatusBadge status={row.publicationStatus} />
                  <AvailabilityBadge status={row.availabilityStatus} />
                </div>
              </td>

              <td className="dio-table__actions">
                <div className="dio-actions">
                  <Link to={`${LIST}/${row.id}`} className="dio-btn dio-btn--secondary dio-btn--sm">
                    {canEdit ? t('common.edit') : t('common.details')}
                  </Link>
                  <RowActions
                    status={row.publicationStatus}
                    translation={row.translation}
                    canEdit={canEdit}
                    busy={lifecycle.busy}
                    // Named after the record, so a screen-reader user hears
                    // which row's menu this is rather than forty-four identical
                    // "More actions" buttons.
                    label={t('common.openMenuFor', {
                      name: row.name[locale] || row.name.ar || row.name.en,
                    })}
                    extra={[
                      {
                        label:
                          row.availabilityStatus === 'AVAILABLE'
                            ? t('items.markUnavailable')
                            : t('items.markAvailable'),
                        icon: <Ban className="dio-icon" aria-hidden="true" />,
                        onSelect: () =>
                          availability.mutate({
                            row,
                            next: row.availabilityStatus === 'AVAILABLE' ? 'UNAVAILABLE' : 'AVAILABLE',
                          }),
                      },
                    ]}
                    handlers={{
                      onPublish: () => lifecycle.publish.mutate({ id: row.id, version: row.version }),
                      onUnpublish: () => lifecycle.unpublish.mutate({ id: row.id, version: row.version }),
                      onArchive: () => lifecycle.archive.mutate({ id: row.id, version: row.version }),
                      onRestore: () => lifecycle.restore.mutate({ id: row.id, version: row.version }),
                    }}
                  />
                </div>
              </td>
            </tr>
          ))}
        </DataTable>
      )}

      <Pagination
        page={params.page ?? 1}
        pageSize={params.pageSize ?? 20}
        total={list.total}
        onPage={(page) => setParams({ page })}
      />

    </>
  );
}

/**
 * Create and edit, each at its own address.
 *
 * One component serves both: `/menu/items/new` mounts the form with no record,
 * `/menu/items/<id>` fetches the record first. Splitting them would mean two
 * copies of a form whose only difference is which verb it ends in.
 */
export function ItemFormPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { can } = useAuth();
  const canEdit = can('OWNER', 'EDITOR');

  const categories = useCategoryOptions();
  const record = useRecord<AdminItem>(qk.item(id ?? ''), URL, id);

  const title = id ? t('items.editTitle') : t('items.newTitle');

  // A new dish's category defaults to the first one, so the form cannot be
  // mounted before the list is here to default out of.
  const options = categories.data?.data;
  if (!options) return <FormLoading title={title} backTo={LIST} />;

  if (!id) return <ItemForm item={null} categories={options} canEdit={canEdit} />;

  return (
    <RecordPage title={title} backTo={LIST} record={record}>
      {(row) => <ItemForm key={row.id} item={row} categories={options} canEdit={canEdit} />}
    </RecordPage>
  );
}

function ItemForm({
  item,
  categories,
  canEdit,
}: {
  item: AdminItem | null;
  categories: AdminCategory[];
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();

  const [name, setName] = useState<LocalizedPair>(item?.name ?? { ar: '', en: '' });
  const [description, setDescription] = useState<LocalizedPair>(editablePair(item?.description));
  const [basePrice, setBasePrice] = useState(item?.basePrice ?? '');
  const [categoryId, setCategoryId] = useState(item?.category.id ?? categories[0]?.id ?? '');
  const [imageId, setImageId] = useState<string | null>(item?.image?.id ?? null);
  const [image, setImage] = useState(item?.image ?? null);
  const [available, setAvailable] = useState(item ? item.availabilityStatus === 'AVAILABLE' : true);

  const dirty = useDirty({ name, description, basePrice, categoryId, imageId, available });

  const save = useAdminMutation(
    () => {
      const body = {
        categoryId,
        nameAr: name.ar,
        nameEn: name.en,
        descriptionAr: description.ar,
        descriptionEn: description.en,
        // An empty box means "no price", which is a real state — not "zero".
        basePrice: basePrice.trim() === '' ? undefined : basePrice.trim(),
        imageId,
      };
      return item
        ? apiPatch(`${URL}/${item.id}`, {
            ...body,
            expectedVersion: item.version,
            availabilityStatus: available ? 'AVAILABLE' : 'UNAVAILABLE',
          })
        : apiPost(URL, body);
    },
    {
      successMessage: item ? t('common.saved') : t('common.created'),
      invalidate: INVALIDATE,
      onSuccess: () => navigate(LIST),
    },
  );

  return (
    <FormPage
      title={item ? t('items.editTitle') : t('items.newTitle')}
      description={t('items.subtitle')}
      backTo={LIST}
      dirty={dirty}
      saving={save.isPending}
      canSave={name.ar.trim().length > 0 && Boolean(categoryId)}
      onSave={canEdit ? () => save.mutate(undefined) : undefined}
    >
      {item && <PublishBlockers translation={item.translation} />}
      {item && <PublishWarnings translation={item.translation} />}

      <Field label={t('items.category')} htmlFor="item-category" required>
        <Select
          id="item-category"
          value={categoryId}
          disabled={!canEdit}
          onChange={(event) => setCategoryId(event.target.value)}
        >
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name[locale] || category.name.ar || category.name.en}
            </option>
          ))}
        </Select>
      </Field>

      <BilingualField
        label={t('items.name')}
        value={name}
        onChange={setName}
        required
        maxLength={140}
        disabled={!canEdit}
      />

      <BilingualField
        label={t('items.description')}
        value={description}
        onChange={setDescription}
        multiline
        maxLength={1000}
        disabled={!canEdit}
      />

      <Field label={t('items.basePrice')} htmlFor="item-price" hint={t('items.priceHint')}>
        <Input
          id="item-price"
          type="text"
          inputMode="decimal"
          dir="ltr"
          className="tabular"
          disabled={!canEdit}
          value={basePrice}
          placeholder="0.00"
          onChange={(event) => setBasePrice(event.target.value)}
        />
      </Field>

      <ImagePicker
        label={t('items.image')}
        hint={t('items.imageHint')}
        surface="item"
        value={image}
        disabled={!canEdit}
        onChange={(picked) => {
          setImageId(picked?.id ?? null);
          setImage(picked ? { ...picked, storageKey: '', alt: { ar: '', en: '' } } : null);
        }}
      />

      {item && (
        <>
          <Switch
            checked={available}
            onChange={setAvailable}
            disabled={!canEdit}
            label={t('items.availability')}
            hint={t('items.availabilityHint')}
          />
          <AddonGroupAssignment item={item} canEdit={canEdit} />
        </>
      )}
    </FormPage>
  );
}

/**
 * Which add-on groups appear on this dish.
 *
 * The API takes the complete ordered list and removes anything absent, so this
 * sends every checked group every time rather than a delta.
 */
function AddonGroupAssignment({ item, canEdit }: { item: AdminItem; canEdit: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [selected, setSelected] = useState<string[]>(item.addonGroups.map((group) => group.id));

  const groups = useQuery({
    queryKey: qk.addonGroups({ all: true }),
    queryFn: () => apiGet<AdminAddonGroup[]>('/admin/addon-groups', { page: 1, pageSize: 100 }),
  });

  const assign = useAdminMutation(
    () => apiPut(`${URL}/${item.id}/addon-groups`, { groupIds: selected, expectedVersion: item.version }),
    { successMessage: t('common.saved'), invalidate: INVALIDATE },
  );

  return (
    <div className="dio-field">
      <span className="dio-label">{t('items.addonGroups')}</span>
      <p className="dio-hint">{t('items.addonGroupsHint')}</p>

      {groups.isLoading ? (
        <SkeletonRows rows={2} />
      ) : (
        <div className="flex flex-col gap-2">
          {(groups.data?.data ?? []).map((group) => (
            <div key={group.id} className="flex items-center gap-2">
              <Checkbox
                checked={selected.includes(group.id)}
                disabled={!canEdit}
                onChange={(checked) =>
                  setSelected((current) =>
                    checked ? [...current, group.id] : current.filter((id) => id !== group.id),
                  )
                }
                label={group.name[locale] || group.name.ar || group.name.en}
              />
              <StatusBadge status={group.publicationStatus} />
            </div>
          ))}
        </div>
      )}

      {canEdit && (
        <Button size="sm" className="mt-2" loading={assign.isPending} onClick={() => assign.mutate(undefined)}>
          {t('items.saveAddonGroups')}
        </Button>
      )}
    </div>
  );
}
